const readingKey = (topicSlug, readingId) => `${topicSlug}/${readingId}`;
const readingNodeId = (topicSlug, readingId) => `reading:${topicSlug}:${readingId}`;

// These are editorial layer labels, not claims about universal Christian agreement.
export const FOUNDATION_LAYERS = [
    { id: 'first-principle', label: 'First-principle commitment' },
    { id: 'doctrinal-framework', label: 'Doctrinal framework' },
    { id: 'interpretive-principle', label: 'Interpretive principle' },
    { id: 'applied-judgement', label: 'Applied judgement' },
    { id: 'unclassified', label: 'Not yet classified' },
];

/**
 * Build a multi-parent graph from the authored perspective data.
 * Predicate commitments are kept as labelled dependency edges; other reading
 * relations remain separate so they cannot accidentally become foundations.
 */
export const buildPerspectiveFoundationGraph = (topics = []) => {
    const readingsByKey = new Map();
    const nodes = [];

    topics.forEach((topic) => (topic.readings || []).forEach((reading) => {
        const key = readingKey(topic.slug, reading.id);
        const node = {
            id: readingNodeId(topic.slug, reading.id),
            key,
            topicSlug: topic.slug,
            readingId: reading.id,
            label: reading.title,
            category: topic.category,
            href: `/perspectives/${topic.slug}#${reading.id}`,
            reading,
        };
        readingsByKey.set(key, node);
        nodes.push(node);
    }));

    const foundationEdges = [];
    const unlinkedClaims = [];
    const perspectiveRelations = [];
    topics.forEach((topic) => (topic.readings || []).forEach((reading) => {
        const source = readingNodeId(topic.slug, reading.id);
        (reading.predicates || []).forEach((predicate, index) => {
            const targetKey = predicate.topicSlug && predicate.readingId
                ? readingKey(predicate.topicSlug, predicate.readingId)
                : null;
            // An explicit linked:false must remain unlinked even if stale
            // target fields happen to be present in the source data.
            const target = targetKey && predicate.linked !== false ? readingsByKey.get(targetKey) : null;
            const claim = {
                id: predicate.canonicalId || `predicate:${predicate.label}`,
                label: predicate.label,
                layer: predicate.foundationLayer || 'unclassified',
                relationType: 'depends-on',
                argument: predicate.argument || null,
                linked: Boolean(target),
            };

            if (target) {
                foundationEdges.push({
                    id: `${source}|${claim.id}|${target.id}|${index}`,
                    source,
                    target: target.id,
                    claim,
                });
            } else {
                unlinkedClaims.push({ id: `${source}|${claim.id}|${index}`, source, claim });
            }
        });

        (reading.relations || []).forEach((relation, index) => {
            let targetKey = relation.target ? readingKey(topic.slug, relation.target) : null;
            if (relation.topicSlug && relation.readingId) targetKey = readingKey(relation.topicSlug, relation.readingId);
            const target = targetKey ? readingsByKey.get(targetKey) : null;
            perspectiveRelations.push({
                id: `${readingNodeId(topic.slug, reading.id)}|${relation.type}|${target?.id || relation.topicSlug || relation.target || index}|${index}`,
                source: readingNodeId(topic.slug, reading.id),
                target: target?.id || null,
                type: relation.type,
                label: relation.label || target?.label || relation.target || 'Unlinked relation',
                linked: Boolean(relation.linked && target),
            });
        });
    }));

    return { nodes, foundationEdges, unlinkedClaims, perspectiveRelations };
};

/**
 * Keep explicit predicate claims with no verified reading target visible as
 * root claims. They connect to the perspectives that record them, but do not
 * receive an invented parent or get merged into a tradition-specific reading.
 */
export const buildRecordedPredicateLayer = (unlinkedClaims = []) => {
    const nodesById = new Map();
    const edges = unlinkedClaims.map((item) => {
        if (!nodesById.has(item.claim.id)) {
            nodesById.set(item.claim.id, {
                id: item.claim.id,
                type: 'predicate',
                label: item.claim.label,
                subtitle: 'Explicit commitment · no deeper parent assigned',
                category: 'Recorded predicates',
                explanation: 'This commitment is explicitly recorded for the perspective. No narrower foundational reading is linked yet, so it appears as a root predicate without an invented parent.',
                reading: { badges: [] },
                href: null,
            });
        }
        return {
            id: `${item.id}|recorded-predicate`,
            source: item.source,
            target: item.claim.id,
            claim: {
                ...item.claim,
                relationType: 'depends-on',
                argument: 'Recorded predicate claim; a narrower foundational premise has not been verified.',
                recordedOnly: true,
            },
        };
    });
    return { nodes: [...nodesById.values()], edges };
};

/**
 * Materialise each authored predicate as a graph node so the map can show the
 * actual reasoning step between a perspective and the reading it cites as a
 * foundation. The predicate remains the semantic unit; the source/target
 * readings are not joined directly and the claim text is never discarded.
 */
export const buildPredicateDependencyLayer = (graph) => {
    const nodesById = new Map();
    const edges = [];
    const addClaim = (source, claim, target, occurrenceId) => {
        const id = `predicate:${claim.id}`;
        if (!nodesById.has(id)) {
            nodesById.set(id, {
                id,
                type: 'predicate',
                label: claim.label,
                subtitle: claim.linked && target ? 'Recorded predicate · linked foundation' : 'Recorded predicate · no deeper parent assigned',
                category: 'Recorded predicates',
                explanation: claim.argument || (target
                    ? 'This explicit predicate is recorded as a reason for the perspective and linked to the foundation reading shown on the map.'
                    : 'This commitment is explicitly recorded for the perspective. No narrower foundational reading is linked yet, so it appears as a root predicate without an invented parent.'),
                reading: { badges: [] },
                href: null,
                targetId: target || null,
                targetIds: [],
            });
        }
        if (target && !nodesById.get(id).targetIds.includes(target)) nodesById.get(id).targetIds.push(target);
        edges.push({
            id: `${occurrenceId}|depends-on-predicate`,
            source,
            target: id,
            kind: 'depends-on',
            claim: { ...claim, relationType: 'depends-on' },
        });
        if (target) {
            edges.push({
                id: `${occurrenceId}|predicate-foundation`,
                source: id,
                target,
                kind: 'grounds',
                claim: { ...claim, relationType: 'grounds' },
            });
        }
    };

    graph.foundationEdges.forEach((edge) => addClaim(edge.source, edge.claim, edge.target, edge.id));
    graph.unlinkedClaims.forEach((item) => addClaim(item.source, item.claim, null, item.id));
    return { nodes: [...nodesById.values()], edges };
};

// Editorial browsing groups are deliberately separate from authored links.
// Their wording and parent foundations are conservative thematic bridges,
// not claims that a perspective explicitly cites these premises.
export const PREDICATE_THEMES = [
    { id: 'theme-scriptural-reading', label: 'Scripture, interpretation, and received teaching', patterns: [/\bscripture\b|\bbiblical\b|\bhistorical interpretation\b|\bhistorical Christian teaching\b|\bgrammatical\b|\bverse \d+\b|\btext\b|\bimmediate context\b|\brepeated phrases\b|\bwater imagery\b|\bcommandment\b|\bcommandments\b|\b(?:scriptural|biblical|divine) command\b|\breceived norm\b|\bcovenant setting\b|\bdoctrinal continuity\b|\bscientific interpretation\b/i], foundations: ['radial-foundation-scripture-revelation'] },
    { id: 'theme-christ-and-discipleship', label: 'Christ, the kingdom, and discipleship', patterns: [/\bJesus\b|\bChrist(?:like)?\b|\bSon\b|\bLord\b|\bcross\b|\bresurrection\b|\bkingdom\b|\bgospel\b|\bdiscipleship\b|\bthe Word\b/i], foundations: ['radial-foundation-scripture-revelation', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-divine-unity-and-personhood', label: 'Divine unity, personhood, and agency', patterns: [/\bindivisible divine being\b|\bthree distinct divine persons\b|\bdivine personhood\b|\bdivine unity\b|\bpersonal presence and power of the triune God\b|\bGodhead\b/i], foundations: ['radial-foundation-divine-identity'] },
    { id: 'theme-holiness-and-formation', label: 'Holiness, virtue, and moral formation', patterns: [/\bholiness\b|\bholy (?:life|living|character|conduct|formation)\b|\bchastity\b|\btemperance\b|\bself-command\b|\bself-control\b|\bvirtue\b|\bpurity\b/i], foundations: ['radial-foundation-love-of-god-neighbour', 'radial-foundation-scripture-revelation'] },
    { id: 'theme-family-and-embodied-life', label: 'Family, sexuality, and embodied life', patterns: [/\bmarriage\b|\bsexual(?:ity)?\b|\bfertility\b|\bprocreation\b|\bfamily\b|\bpregnan\w*\b|\bembodied\b/i], foundations: ['radial-foundation-image-bearing-dignity', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-human-dignity-and-life', label: 'Human dignity, vulnerability, and life', patterns: [/\bdignity\b|\blife\b|\bperson(?:hood)?\b|\bpeople\b|\bwomen\b|\bchildren\b|\bbirth\b|\bembryonic\b|\bbody\b|\bsuffering\b|\bdying\b|\bdependence\b/i], foundations: ['radial-foundation-image-bearing-dignity', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-creation-and-stewardship', label: 'Creation, work, and material stewardship', patterns: [/\bcreation\b|\bcreature\b|\benvironment\b|\bstewardship\b|\bdominion\b|\bmoney\b|\bpossessions\b|\bwealth\b|\beconomic\b|\bcontract\b|\bdebt\b|\bcreditor\b|\blabour\b|\bvocation\b|\bresource\w*\b|\baddiction\b|\bconsumer\w*\b|\bconsumption\b|\bcreated goods\b/i], foundations: ['radial-foundation-image-bearing-dignity', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-church-and-public-life', label: 'Church, community, and public responsibility', patterns: [/\bchurch\b|\becclesial\b|\bworship\b|\bcommunity\b|\bcommunal\b|\bpastoral\b|\bauthority\b|\btradition\b|\bcitizenship\b|\bpublic\b|\blaw\b|\binstitution\b|\bhistorical Christian teaching\b/i], foundations: ['radial-foundation-scripture-revelation', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-neighbour-justice-and-mercy', label: 'Neighbour-love, justice, and mercy', patterns: [/\bneighbou?r\b|\benemy-love\b|\blove of God\b|\bjustice\b|\binjustice\b|\bmercy\b|\bcompassion\b|\bservice\b|\bpoor\b|\bvulnerable\b|\bcare\b|\bcares\b|\bcared\b|\bcaring\b|\bsafety\b|\bprotect(?:ion|s|ed|ing)?\b|\bsocial holiness\b/i], foundations: ['radial-foundation-love-of-god-neighbour', 'radial-foundation-image-bearing-dignity'] },
    { id: 'theme-freedom-and-discernment', label: 'Freedom, conscience, and discernment', patterns: [/\bfreedom\b|\bliberty\b|\bconscience\b|\bdiscernment\b|\bwisdom\b|\bresponsibility\b|\bobligation\b|\bpermissible\b|\buncertainty\b/i], foundations: ['radial-foundation-love-of-god-neighbour', 'radial-foundation-scripture-revelation'] },
    { id: 'theme-truth-and-witness', label: 'Truthfulness, allegiance, and non-coercive witness', patterns: [/\btruth\b|\bspeech\b|\bhonest\w*\b|\bwitness\b|\boath\b|\bpromise\b|\ballegiance\b|\bcoerc\w*\b|\bpower\b|\bviolence\b|\bpeace\b/i], foundations: ['radial-foundation-scripture-revelation', 'radial-foundation-love-of-god-neighbour'] },
    { id: 'theme-spiritual-guidance', label: 'Prayer, spiritual gifts, and guidance', patterns: [/\bprayer\b|\bSpirit\b|\bspiritual\b|\bprophecy\b|\boracle\b|\bsigns?\b|\bdivination\b|silence from God|divine initiative|accommodate weakness|\bpresumption\b|sacred signs|\bimpersonal or controllable force\b/i], foundations: ['radial-foundation-divine-revelation', 'radial-foundation-scripture-revelation'] },
    { id: 'theme-grace-and-faith', label: 'Grace, faith, and spiritual response', patterns: [/\bgrace\b|\bfaith(?:ful(?:ness)?)?\b|\brepentance\b|\brenunciation\b|\bgenerosity\b|\bcommunion\b|\bhumility\b|\bperseverance\b/i], foundations: ['radial-foundation-divine-goodness', 'radial-foundation-scripture-revelation'] },
    { id: 'theme-knowledge-and-creation', label: 'Science, natural explanation, and creation', patterns: [/\bscience\b|\bscientific\b|\bnatural(?:istic)? explanation\b|\bnatural process(?:es)?\b|\bevolution(?:ary)?\b|\bcosmology\b/i], foundations: ['radial-foundation-creation-and-life', 'radial-foundation-divine-revelation'] },
];

// Wording follows the Vatican's English presentation of the Apostles' and
// Niceno-Constantinopolitan Creeds. These are an editorial Christian frame,
// not claims that every mapped perspective cites or formally relies on them.
export const CREEDAL_FOUNDATIONS = [
    { id: 'radial-creed-god-creator', label: 'One God, the Father, creator of all', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bcreator\b|\bmaker of (?:heaven and earth|all things)\b|\bGod (?:created|made) (?:the )?(?:heavens? and earth|world|all things|everything)\b|\bsource of life\b/i], themes: ['theme-scriptural-reading', 'theme-family-and-embodied-life', 'theme-human-dignity-and-life', 'theme-creation-and-stewardship', 'theme-knowledge-and-creation'], references: 'Genesis 1:1; Acts 17:24', explanation: 'Both creeds confess God the Father as creator of heaven and earth. This is placed immediately around the map’s shared “belief in God” hub as a creedal elaboration, not as a claim that each perspective formally depends on either creed.' },
    { id: 'radial-creed-christ-paschal', label: 'Jesus Christ incarnate, crucified, and risen', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bJesus Christ\b|\bChrist Jesus\b|\bincarnation\b|\bincarnate\b|\bcrucifixion\b|\bcrucified\b|\bcross of Christ\b|\bJesus (?:died|rose|was raised|is risen)\b|\bChrist(?:'s|’s) (?:death|resurrection)\b|\bresurrection of Christ\b|\bascension\b|\bascended\b|\bLord Jesus\b|\bJesus is Lord\b|\breturn of Christ\b|\bcoming of Christ\b|\bPaschal\b/i], themes: ['theme-christ-and-discipleship', 'theme-truth-and-witness'], references: 'John 1:14; 1 Corinthians 15:3–4', explanation: 'Both creeds confess Jesus Christ as Lord, his incarnation, crucifixion, resurrection, ascension, and return. The Nicene Creed states this in greater doctrinal detail.' },
    { id: 'radial-creed-son-divine', label: 'The Son is eternally from, and one in being with, the Father', sources: ['Nicene-Constantinopolitan Creed'], patterns: [/\bSon of God\b|\bdivine Son\b|\bone in being\b|\bbegotten\b|\bconsubstantial\b|\bJesus (?:is|as) God\b|\bthe Word\b.*\bdivine nature\b/i], themes: ['theme-christ-and-discipleship'], references: 'John 1:1–3; John 10:30', explanation: 'The Nicene-Constantinopolitan Creed identifies the Son as eternally begotten, not made, and of one Being with the Father. This specifically Nicene formulation is not stated in the Apostles’ Creed.' },
    { id: 'radial-creed-holy-spirit', label: 'The Holy Spirit is confessed as Lord and giver of life', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bFather, Son, and Holy Spirit\b|\bHoly Spirit (?:is|as) (?:the )?(?:Lord|giver of life)\b|\bSpirit.{0,35}(?:the Lord|giver of life|spoken through the prophets)\b/i], themes: ['theme-holiness-and-formation', 'theme-freedom-and-discernment', 'theme-spiritual-guidance'], references: 'John 6:63; 2 Corinthians 3:6', explanation: 'Both creeds confess the Holy Spirit; the Nicene Creed explicitly calls the Spirit “the Lord, the giver of life” and says the Spirit has spoken through the prophets. Procession wording has Eastern and Western forms; this map does not adjudicate them.' },
    { id: 'radial-creed-church', label: 'The Church is holy and catholic; Nicene adds apostolic', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bone holy catholic church\b|\bone holy catholic and apostolic church\b|\bcommunion of saints\b|\bcatholic church\b|\bapostolic church\b/i], themes: ['theme-church-and-public-life'], references: 'Ephesians 4:4–6; 1 Timothy 3:15', explanation: 'Both confess one holy catholic Church; the Nicene-Constantinopolitan Creed additionally says “apostolic.” The Apostles’ Creed also names the communion of saints.' },
    { id: 'radial-creed-forgiveness', label: 'God forgives sins', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bforgiveness of sins\b|\bGod forgives sins\b|\bsins? (?:are|can be) forgiven\b/i], themes: ['theme-neighbour-justice-and-mercy', 'theme-grace-and-faith'], references: 'Luke 24:47; Ephesians 1:7', explanation: 'Both creeds include forgiveness of sins, with the Nicene Creed placing the confession alongside one baptism. This is an interpretive frame for grace-related themes, not an authored dependency.' },
    { id: 'radial-creed-resurrection-life', label: 'The dead are raised; life everlasting is promised', sources: ['Apostles’ Creed', 'Nicene-Constantinopolitan Creed'], patterns: [/\bresurrection of (?:the )?(?:body|dead)\b|\bthe dead are raised\b|\bthe body will be raised\b|\bimmortal(?:ity)?\b|\blife everlasting\b|\blife of the world to come\b|\bworld to come\b|\brenewed creation\b/i], themes: ['theme-human-dignity-and-life'], references: '1 Corinthians 15:12–22; John 11:25–26', explanation: 'Both creeds confess resurrection and life to come. Wording differs: “resurrection of the body” / “resurrection of the dead”; “life everlasting” / “life of the world to come.”' },
];

export const buildCreedalFoundationLayer = (themeNodes = [], predicateNodes = []) => {
    const themeIds = new Set(themeNodes.map((node) => node.id));
    const matchedCreeds = CREEDAL_FOUNDATIONS.filter((creed) => creed.themes.some((themeId) => themeIds.has(themeId))
        || predicateNodes.some((predicate) => creed.patterns.some((pattern) => pattern.test(predicate.label))));
    const nodes = matchedCreeds.map((creed) => ({
        id: creed.id,
        type: 'foundation',
        label: creed.label,
        subtitle: `Creedal foundation · ${creed.sources.join(' + ')}`,
        category: 'Creedal foundations',
        explanation: creed.explanation,
        references: creed.references,
        creedSources: creed.sources,
        creedSourceUrl: 'https://www.vatican.va/content/catechism/en/part_one/section_one/chapter_three/article_2/the_credo.html',
        reading: { badges: [] },
        href: null,
        dependsOn: [],
    }));
    const creedById = new Map(nodes.map((node) => [node.id, node]));
    const themeEdges = themeNodes.flatMap((theme) => CREEDAL_FOUNDATIONS
        .filter((creed) => creed.themes.includes(theme.id) && creedById.has(creed.id))
        .map((creed) => ({
            id: `${theme.id}|editorial-creed|${creed.id}`,
            source: theme.id,
            target: creed.id,
            kind: 'depends-on',
            claim: { id: creed.id, label: creed.label, relationType: 'depends-on', argument: 'Editorial creedal framework link for browsing; not an authored dependency claim.', editorial: true, creedal: true },
        })));
    const predicateEdges = predicateNodes.flatMap((predicate) => CREEDAL_FOUNDATIONS
        .filter((creed) => creed.patterns.some((pattern) => pattern.test(predicate.label)) && creedById.has(creed.id))
        .map((creed) => ({
            id: `${predicate.id}|editorial-creed|${creed.id}`,
            source: predicate.id,
            target: creed.id,
            kind: 'depends-on',
            claim: { id: creed.id, label: creed.label, relationType: 'depends-on', argument: 'The recorded predicate wording directly names a creed-level topic; this additional route is an editorial framework link, not an authored source citation.', editorial: true, creedal: true, directWordingMatch: true },
        })));
    return { nodes, themeEdges, predicateEdges };
};

// These inferred links place broad chart principles just outside the creedal
// ring. They express coherence, not premises explicitly cited by every reading.
export const CREEDAL_FIRST_PRINCIPLE_LINKS = {
    'radial-foundation-creation-and-life': ['radial-creed-god-creator'],
    'radial-foundation-divine-revelation': ['radial-creed-holy-spirit', 'radial-creed-christ-paschal'],
    'radial-foundation-divine-goodness': ['radial-creed-forgiveness'],
};

export const buildPredicateThemeLayer = (predicateNodes = []) => {
    const themesById = new Map();
    const edges = [];
    predicateNodes.forEach((predicate) => {
        if (predicate.targetIds?.length) return;
        const matchingThemes = PREDICATE_THEMES.filter((candidate) => candidate.patterns.some((pattern) => pattern.test(predicate.label)));
        matchingThemes.forEach((theme) => {
            if (!themesById.has(theme.id)) {
                themesById.set(theme.id, {
                    id: theme.id,
                    type: 'foundation',
                    label: theme.label,
                    subtitle: 'Editorial theme · inferred from predicate wording',
                    category: 'Thematic foundations',
                    explanation: 'This is a provisional browsing group inferred from the wording of recorded predicates. It is not an authored dependency or a claim that every perspective explicitly reasons from this foundation.',
                    reading: { badges: [] },
                    href: null,
                    dependsOn: theme.foundations,
                });
            }
            edges.push({
                id: `${predicate.id}|editorial-theme|${theme.id}`,
                source: predicate.id,
                target: theme.id,
                kind: 'depends-on',
                claim: {
                    id: theme.id,
                    label: theme.label,
                    relationType: 'depends-on',
                    argument: `Editorial browsing association based on the predicate wording and the theme “${theme.label}”; not an authored source link or explicit premise stated by the perspective.`,
                    editorial: true,
                },
            });
        });
    });
    return { nodes: [...themesById.values()], edges };
};

/**
 * Assign stable radial depths without flattening every member of a dependency
 * cycle onto one ring. A deterministic spanning hierarchy is used only for
 * placement inside each strongly-connected component; callers still render
 * every original edge, so no authored route is removed or hidden.
 */
export const assignDependencyLayers = (nodeIds = [], edges = []) => {
    let nextIndex = 0;
    const indexById = new Map();
    const lowLink = new Map();
    const stack = [];
    const onStack = new Set();
    const components = [];
    const adjacency = new Map(nodeIds.map((id) => [id, []]));
    edges.forEach((edge) => {
        if (adjacency.has(edge.source) && adjacency.has(edge.target)) adjacency.get(edge.source).push(edge.target);
    });
    const visit = (id) => {
        indexById.set(id, nextIndex);
        lowLink.set(id, nextIndex);
        nextIndex += 1;
        stack.push(id);
        onStack.add(id);
        (adjacency.get(id) || []).forEach((target) => {
            if (!indexById.has(target)) {
                visit(target);
                lowLink.set(id, Math.min(lowLink.get(id), lowLink.get(target)));
            } else if (onStack.has(target)) {
                lowLink.set(id, Math.min(lowLink.get(id), indexById.get(target)));
            }
        });
        if (lowLink.get(id) === indexById.get(id)) {
            const members = [];
            let member;
            do {
                member = stack.pop();
                onStack.delete(member);
                members.push(member);
            } while (member !== id);
            components.push(members);
        }
    };
    nodeIds.forEach((id) => { if (!indexById.has(id)) visit(id); });

    const componentOf = new Map();
    components.forEach((members, component) => members.forEach((id) => componentOf.set(id, component)));
    const componentDependencies = components.map(() => new Set());
    edges.forEach((edge) => {
        const source = componentOf.get(edge.source);
        const target = componentOf.get(edge.target);
        if (source !== undefined && target !== undefined && source !== target) componentDependencies[source].add(target);
    });
    const componentDepthCache = new Map();
    const componentDepth = (component) => {
        if (componentDepthCache.has(component)) return componentDepthCache.get(component);
        const dependencies = [...componentDependencies[component]];
        const depth = dependencies.length ? 1 + Math.max(...dependencies.map(componentDepth)) : 0;
        componentDepthCache.set(component, depth);
        return depth;
    };
    components.forEach((_, component) => componentDepth(component));

    const depthById = new Map();
    const componentOrder = components.map((members, component) => ({ members, component })).sort((a, b) => componentDepth(a.component) - componentDepth(b.component));
    componentOrder.forEach(({ members, component }) => {
        const memberSet = new Set(members);
        const internalEdges = edges.filter((edge) => memberSet.has(edge.source) && memberSet.has(edge.target));
        const cyclic = members.length > 1 || internalEdges.some((edge) => edge.source === edge.target);
        if (!cyclic) {
            const externalDependencies = edges.filter((edge) => memberSet.has(edge.source) && !memberSet.has(edge.target));
            const depth = externalDependencies.length
                ? 1 + Math.max(...externalDependencies.map((edge) => depthById.get(edge.target) || 0))
                : 0;
            members.forEach((id) => depthById.set(id, depth));
            return;
        }

        const externalEdges = edges.filter((edge) => memberSet.has(edge.source) && !memberSet.has(edge.target));
        const deepestExternalDepth = Math.max(-1, ...externalEdges.map((edge) => depthById.get(edge.target) || 0));
        const anchors = deepestExternalDepth >= 0
            ? [...new Set(externalEdges.filter((edge) => (depthById.get(edge.target) || 0) === deepestExternalDepth).map((edge) => edge.source))].sort()
            : [members.slice().sort()[0]];
        const dependentsByDependency = new Map(members.map((id) => [id, []]));
        internalEdges.forEach((edge) => dependentsByDependency.get(edge.target).push(edge.source));
        dependentsByDependency.forEach((dependents) => dependents.sort());
        const queue = [...anchors];
        const placed = new Set();
        anchors.forEach((id) => { depthById.set(id, deepestExternalDepth + 1); placed.add(id); });
        for (let index = 0; index < queue.length; index += 1) {
            const dependency = queue[index];
            (dependentsByDependency.get(dependency) || []).forEach((dependent) => {
                if (placed.has(dependent)) return;
                depthById.set(dependent, depthById.get(dependency) + 1);
                placed.add(dependent);
                queue.push(dependent);
            });
        }
        members.forEach((id) => {
            if (!depthById.has(id)) depthById.set(id, componentDepth(component));
        });
    });
    return { depthById, maxDepth: Math.max(0, ...depthById.values()) };
};

/** Split unusually wide layout layers into additional rings without removing
 * edges or reversing acyclic depends-on routes. Cycle cross-links remain. */
export const balanceDependencyLayers = (nodeIds = [], edges = [], depthById = new Map(), maxPerLayer = 140) => {
    const layoutDepthById = new Map(nodeIds.map((id) => [id, depthById.get(id) || 0]));
    const inwardHierarchyEdges = edges.filter((edge) => depthById.has(edge.source)
        && depthById.has(edge.target)
        && depthById.get(edge.source) > depthById.get(edge.target));
    const inwardParentsById = new Map(nodeIds.map((id) => [id, []]));
    const dependentCountById = new Map();
    inwardHierarchyEdges.forEach((edge) => {
        inwardParentsById.get(edge.source).push(edge.target);
        dependentCountById.set(edge.target, (dependentCountById.get(edge.target) || 0) + 1);
    });
    // Keep nodes with a shared, nearest foundation together when a crowded
    // dependency level has to be split across layout rings. Alphabetical
    // batching separated related perspectives and predicates into arbitrary
    // rings, making their branches look unrelated.
    const branchKey = (id) => {
        const parents = inwardParentsById.get(id) || [];
        const nearestDepth = Math.max(-1, ...parents.map((parent) => layoutDepthById.get(parent) ?? -1));
        const nearestParents = parents.filter((parent) => layoutDepthById.get(parent) === nearestDepth);
        nearestParents.sort((a, b) => (dependentCountById.get(b) || 0) - (dependentCountById.get(a) || 0) || String(a).localeCompare(String(b)));
        return nearestParents[0] || '';
    };
    const iterationLimit = Math.max(8, nodeIds.length * 2);
    for (let iteration = 0; iteration < iterationLimit; iteration += 1) {
        const groups = new Map();
        nodeIds.forEach((id) => {
            const depth = layoutDepthById.get(id);
            if (!groups.has(depth)) groups.set(depth, []);
            groups.get(depth).push(id);
        });
        let splitLayer = false;
        [...groups.entries()].sort(([a], [b]) => a - b).forEach(([depth, ids]) => {
            if (ids.length <= maxPerLayer) return;
            ids.sort((a, b) => String(branchKey(a)).localeCompare(String(branchKey(b))) || String(a).localeCompare(String(b)));
            ids.forEach((id, index) => {
                const nextDepth = depth + Math.floor(index / maxPerLayer);
                if (nextDepth > layoutDepthById.get(id)) {
                    layoutDepthById.set(id, nextDepth);
                    splitLayer = true;
                }
            });
        });
        // If a dependency is moved outward, push its dependents outward too.
        let movedDependent = true;
        while (movedDependent) {
            movedDependent = false;
            inwardHierarchyEdges.forEach((edge) => {
                const minimumSourceDepth = layoutDepthById.get(edge.target) + 1;
                if (layoutDepthById.get(edge.source) < minimumSourceDepth) {
                    layoutDepthById.set(edge.source, minimumSourceDepth);
                    movedDependent = true;
                }
            });
        }
        const counts = new Map();
        layoutDepthById.forEach((depth) => counts.set(depth, (counts.get(depth) || 0) + 1));
        const largestLayer = Math.max(0, ...counts.values());
        if (!splitLayer && largestLayer <= maxPerLayer) break;
    }
    return { depthById: layoutDepthById, maxDepth: Math.max(0, ...layoutDepthById.values()) };
};

/**
 * Order a radial ring by its nearest inward dependency branch. Siblings stay
 * adjacent around the circumference; branches themselves follow the angle of
 * the foundations they depend on. This changes placement only, never edges.
 */
export const orderDependencyLayer = (nodeIds = [], edges = [], depthById = new Map(), angleById = new Map(), labelById = new Map()) => {
    const parentsById = new Map(nodeIds.map((id) => [id, []]));
    const dependentCount = new Map();
    edges.forEach((edge) => {
        if (!parentsById.has(edge.source)) return;
        const sourceDepth = depthById.get(edge.source);
        const targetDepth = depthById.get(edge.target);
        if (sourceDepth === undefined || targetDepth === undefined || targetDepth >= sourceDepth) return;
        parentsById.get(edge.source).push(edge.target);
        dependentCount.set(edge.target, (dependentCount.get(edge.target) || 0) + 1);
    });

    const branches = new Map();
    nodeIds.forEach((id) => {
        const parents = parentsById.get(id) || [];
        const nearestDepth = Math.max(-1, ...parents.map((parent) => depthById.get(parent) ?? -1));
        const nearestParents = parents.filter((parent) => depthById.get(parent) === nearestDepth);
        nearestParents.sort((a, b) => (dependentCount.get(b) || 0) - (dependentCount.get(a) || 0) || String(a).localeCompare(String(b)));
        const parent = nearestParents[0] || null;
        const key = parent || `root:${id}`;
        if (!branches.has(key)) branches.set(key, { parent, ids: [] });
        branches.get(key).ids.push(id);
    });

    const ringOffset = Math.PI / 2;
    const angleFromTop = (angle) => ((angle + ringOffset) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    const branchAngle = ({ parent, ids }) => {
        const angles = parent ? [angleById.get(parent)].filter((angle) => angle !== undefined) : [];
        if (angles.length) return angleFromTop(angles[0]);
        const memberAngles = ids.flatMap((id) => (parentsById.get(id) || []).map((parentId) => angleById.get(parentId)).filter((angle) => angle !== undefined));
        if (!memberAngles.length) return null;
        const x = memberAngles.reduce((sum, angle) => sum + Math.cos(angle), 0);
        const y = memberAngles.reduce((sum, angle) => sum + Math.sin(angle), 0);
        return angleFromTop(Math.atan2(y, x));
    };
    return [...branches.values()]
        .sort((a, b) => {
            const aAngle = branchAngle(a);
            const bAngle = branchAngle(b);
            if (aAngle !== null && bAngle !== null && aAngle !== bAngle) return aAngle - bAngle;
            if (aAngle !== null) return -1;
            if (bAngle !== null) return 1;
            return String(labelById.get(a.ids[0]) || a.ids[0]).localeCompare(String(labelById.get(b.ids[0]) || b.ids[0]));
        })
        .flatMap((branch) => branch.ids.sort((a, b) => String(labelById.get(a) || a).localeCompare(String(labelById.get(b) || b))));
};

/**
 * Trace only the inward, foundation-seeking branches from selected nodes.
 * Same-ring and outward edges remain in the graph as visible cross-links, but
 * cannot send a highlighted route back out through a dependency cycle.
 */
export const traceDependencyRoutes = (startIds = [], edges = [], depthById = new Map()) => {
    const outgoing = new Map();
    edges.forEach((edge) => {
        if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
        outgoing.get(edge.source).push(edge);
    });
    const nodeIds = new Set();
    const edgeIds = new Set();
    const visited = new Set();
    const visit = (id) => {
        if (visited.has(id)) return;
        visited.add(id);
        nodeIds.add(id);
        (outgoing.get(id) || []).forEach((edge) => {
            const sourceDepth = depthById.get(edge.source);
            const targetDepth = depthById.get(edge.target);
            if (sourceDepth === undefined || targetDepth === undefined || targetDepth >= sourceDepth) return;
            edgeIds.add(edge.id);
            nodeIds.add(edge.target);
            visit(edge.target);
        });
    };
    startIds.forEach(visit);
    return { nodeIds, edgeIds };
};
